<template>
    <RedesignBaseChart
        ref="baseChartRef"
        :option="option"
        :widget="data"
        :hide="!chartSeriesData"
        :classes="chartClasses"
    >
        <template #pre>
            <div
                v-if="!chartSeriesData"
                class="flex flex-col items-center w-full h-full justify-center"
            >
                <icon-no-data-funnel class="max-h-96" />
            </div>
        </template>

        <template #post>
            <div
                v-if="showAutoDialerTable && chartSeriesData"
                class="my-4 px-4 w-full"
            >
                <DialerTable
                    :editable="editable"
                    :data="data"
                    :dialers="dialersWithData"
                    :table-data-loaded="dataFromRequest"
                    @update-widget="updateWidget"
                />
            </div>
        </template>
    </RedesignBaseChart>
</template>

<script lang="ts" setup>
import { computed, ref, watch, nextTick, toRef } from 'vue'
import { currentAccountEntitiesCampaigns } from '@/composables/globalData/useEntitiesData'
import useDialers from '@/composables/useDialers'
import type { ComponentExposed } from 'vue-component-type-helpers'
import { ComposeOption } from 'echarts/core'
import { FunnelSeriesOption } from 'echarts/charts'
import {
    DatasetComponentOption,
    GridComponentOption,
    LegendComponentOption,
    TitleComponentOption,
    TooltipComponentOption,
} from 'echarts/components'

import { TranslateResult } from 'vue-i18n'

import { WidgetExtended } from '@/types/dashboard'
import { DialerStatisticType } from '@/types/dialers'

import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import DialerTable from '@/components/Charts/FunnelChart/DialerTable.vue'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'

import { useI18n } from 'vue-i18n'

const { t } = useI18n()
import { CAMPAIGN_IVR_LIST_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'

/* Types */
interface ChartSeriesData {
    value: number
    name: TranslateResult
    display?: string
    statisticType: DialerStatisticType
}

type ECOption = ComposeOption<
    | FunnelSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
    | DatasetComponentOption
>

/* Props */
const props = withDefaults(
    defineProps<{
        data: WidgetExtended
        editable?: boolean
    }>(),
    {
        editable: true
    }
)

const emit = defineEmits<{
    (event: 'on-update', widgetData: WidgetExtended): void
}>()

/* Data */
const HORIZONTAL_ORIENT = 'horizontal'
const VERTICAL_ORIENT = 'vertical'
const baseChartRef = ref<ComponentExposed<typeof RedesignBaseChart>>()
const colorList = [
    '#999999', // Pending calls (gray)
    '#3B82F6', // Originating (blue)
    '#60A5FA', // Leg1 Ring (lighter blue)
    '#10B981', // Leg1 Answer (green)
    '#059669', // Leg2 Ring
    '#16A34A'  // Leg2 Answer (darker green)
]

const widgetData = toRef(props, 'data')
const { dialersWithTypeIVR } = useDialers()

/* Computed */
const getAllDialersWithTypeIVR = computed(() => {
    return dialersWithTypeIVR.value.filter(({ campaignID }) => {
        return !!currentAccountEntitiesCampaigns.value?.[campaignID]
    })
})

const { data: loadedData } = useWidgetDataWithRefreshInterval(props, 'data')
const { filteredItems: filteredDialers } = useEntityPositiveNegativeFilters(widgetData, CAMPAIGN_IVR_LIST_PARAMETER_NAME, getAllDialersWithTypeIVR, 'campaignID')

const orient = computed(() => {
    const horizontally = props.data.WidgetLayout?.rotateChartHorizontally ?? false

    return horizontally
        ? HORIZONTAL_ORIENT
        : VERTICAL_ORIENT
})
const chartClasses = computed(() => {
    return orient.value === HORIZONTAL_ORIENT ? 'h-1/2' : 'h-96'
})
const chartSizing = computed(() => {
    if (orient.value === HORIZONTAL_ORIENT) {
        return {
            width: '94%',
            left: 'center',
        }
    } else {
        return {
            width: '250',
            left: 'center',
        }
    }
})

const showAutoDialerTable = computed(() => {
    return props.data.WidgetLayout?.showAutoDialerTable ?? true
})
const dialersWithData = computed(() => {

    const allDialersWithData = filteredDialers.value
        .filter(el => el.data && Object.keys(el.data).length)

    if (!allDialersWithData || allDialersWithData.length === 0) {
        return
    }

    return allDialersWithData
})

const dataFromRequest = computed(() => {
    if (!loadedData.value) {
        return []
    }
    return loadedData.value
})

const chartSeriesData = computed<Array<ChartSeriesData> | undefined>(() => {
    if (!dialersWithData.value) {
        return
    }

    const mappedDialers = dialersWithData.value
        .reduce(
            (acc, currentValue) => {
                return {
                    TotalCalls: acc.TotalCalls + currentValue.data.CallStatistics.TotalCalls,
                    TotalLeg1AnswerCalls: acc.TotalLeg1AnswerCalls + currentValue.data.CallStatistics.TotalLeg1AnswerCalls,
                    TotalLeg1RingingCalls: acc.TotalLeg1RingingCalls + currentValue.data.CallStatistics.TotalLeg1RingingCalls,
                    TotalLeg2AnswerCalls: acc.TotalLeg2AnswerCalls + currentValue.data.CallStatistics.TotalLeg2AnswerCalls,
                    TotalLeg2RingingCalls: acc.TotalLeg2RingingCalls + currentValue.data.CallStatistics.TotalLeg2RingingCalls,
                    TotalOriginatingCalls: acc.TotalOriginatingCalls + currentValue.data.CallStatistics.TotalOriginatingCalls,
                    TotalPendingCalls: acc.TotalPendingCalls + currentValue.data.CallStatistics.TotalPendingCalls
                }
            },
            {
                TotalCalls: 0,
                TotalLeg1AnswerCalls: 0,
                TotalLeg1RingingCalls: 0,
                TotalLeg2AnswerCalls: 0,
                TotalLeg2RingingCalls: 0,
                TotalOriginatingCalls: 0,
                TotalPendingCalls: 0,
            }
        )

    if (Object.values(mappedDialers).every(el => el === 0)) {
        return
    }

    const maxTotalPendingCalls = props.data.WidgetLayout?.maxPendingCallDisplay ?? 50

    const totalPendingCalls = mappedDialers.TotalPendingCalls > maxTotalPendingCalls
        ? maxTotalPendingCalls
        : mappedDialers.TotalPendingCalls

    const totalPendingCallsDisplay = mappedDialers.TotalPendingCalls > maxTotalPendingCalls
        ? `${maxTotalPendingCalls}+`
        : `${totalPendingCalls}`

    return [
        {
            value: totalPendingCalls,
            name: t('dialer.totalPendingCalls'),
            display: totalPendingCallsDisplay,
            statisticType: 'TotalPendingCalls'
        },
        {
            value: mappedDialers.TotalOriginatingCalls,
            name: t('dialer.totalOriginatingCalls'),
            statisticType: 'TotalOriginatingCalls'
        },
        {
            value: mappedDialers.TotalLeg1RingingCalls,
            name: t('dialer.totalLeg1RingingCalls'),
            statisticType: 'TotalLeg1RingingCalls'
        },
        {
            value: mappedDialers.TotalLeg1AnswerCalls,
            name: t('dialer.totalLeg1AnswerCalls'),
            statisticType: 'TotalLeg1AnswerCalls'
        },
        {
            value: mappedDialers.TotalLeg2RingingCalls,
            name: t('dialer.totalLeg2RingingCalls'),
            statisticType: 'TotalLeg2RingingCalls'
        },
        {
            value: mappedDialers.TotalLeg2AnswerCalls,
            name: t('dialer.totalLeg2AnswerCalls'),
            statisticType: 'TotalLeg2AnswerCalls'
        }
    ]
})
const option = computed<ECOption | undefined>(() => {
    const getAllCampaignNamesByStatus = (key: DialerStatisticType) => {
        if (!dialersWithData.value) {
            return
        }

        return dialersWithData.value
            .reduce<Array<string>>(
                (acc, el) => {
                    if (el.data.CallStatistics[key] > 0) {
                        return [
                            ...acc,
                            `${el.name} (${el.data.CallStatistics[key]})`
                        ]
                    }

                    return acc
                },
                []
            )
            .join(', ')
    }

    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'item',
            formatter: function (params) {
                if (Array.isArray(params)) {
                    return ''
                }

                const data = params.data as ChartSeriesData

                const campaignNames = getAllCampaignNamesByStatus(data.statisticType)

                return campaignNames
                    ? campaignNames
                    : `${params.name}: ${data.display || params.value}`
            }
        },
        legend: {
            show: false
        },
        series: [
            {
                name: t('dialer.totals') as string,
                type: 'funnel',
                ...chartSizing.value,
                minSize: '10%',
                maxSize: '100%',
                sort: 'none',
                gap: 1,
                orient: orient.value,
                label: {
                    show: true,
                    formatter: function (params) {
                        const data = params.data as ChartSeriesData

                        const displayValue = data.display || data.value

                        return `${params.name} (${displayValue})`
                    }
                },
                data: []
            }
        ]
    }
})

/* Methods */
const updateWidget = (widgetData: WidgetExtended) => {
    emit('on-update', widgetData)
}

function tryPrintChart () {
    console.log('tryPrintChart')
    // TODO: maybe update the base chart functionality to be able to accept some additional html or imagery to append to downloaded content, we need table to be there as well
    // const divToPrint = this.$el.children[0]
    // const newWin = window.open()
    // newWin.document.write(divToPrint.innerHTML)
    // newWin.document.close()
    // newWin.focus()
    // newWin.print()
}
function tryDownloadChart () {
    console.log('tryDownloadChart')
    // if (!this.$refs['funnel-chart']) {
    //     this.$notify.add({
    //         type: 'info',
    //         title: this.$t('dialer.no.calls')
    //     })
    //     return
    // }
    // this.$refs['funnel-chart'].chart.exportChart({
    //     type: type
    // })
}

/* Watch */
watch(
    chartSeriesData,
    (val) => {
        if (!val || !baseChartRef.value) {
            return
        }

        baseChartRef.value.updateSeries(
            [
                {
                    data: val.map((slice, i) => ({
                        ...slice,
                        itemStyle: {
                            color: colorList[i]
                        }
                    }))
                }
            ]
        )
    },
    {
        deep: true,
        immediate: true
    }
)
watch(
    orient,
    async () => {
        await nextTick()
        baseChartRef.value?.resizeChart()
    }
)
</script>
